/**
 * Runtime selector demo↔conectado de la Capa Operacional AI-First.
 *
 * Resuelve, según `VITE_ANC_DEMO` y `VITE_ANC_RF_BASE`, qué
 * implementación de cada interfaz de servicio de la Capa
 * (`contracts.ts`: {@link EventStore}, {@link Graph},
 * {@link MotorCasos}, {@link MotorInsights}, {@link Registry},
 * {@link MotorContextoIA}, {@link Copilot}, {@link RAG},
 * {@link Memoria}, {@link Twin}, {@link FieldOps}, {@link Realtime},
 * {@link Obs}) se utiliza:
 *
 *  - **DEMO** (`VITE_ANC_DEMO=1`, por defecto): implementación
 *    client-side registrada por las tareas posteriores (1.3, 1.7, 2.x…).
 *  - **CONECTADO** (`∃ VITE_ANC_RF_BASE`): stub detrás del **mismo
 *    contrato**, normalmente sobre la capa de adaptadores
 *    (`adapters.ts`); degrada a DEMO ante ausencia o fallo (R17.6).
 *
 * Las implementaciones concretas **todavía no existen**: se registran de
 * forma **perezosa** vía {@link provideDemo} / {@link provideConnected}
 * cuando las tareas que las construyen se ejecuten. Hasta entonces, el
 * runtime devuelve un **stub seguro** ("no implementado aún") que jamás
 * rompe el build ni lanza a través de la frontera de servicio: cada
 * método devuelve un {@link Result} de error **degradado controlado**
 * (R17.6). Esto permite enchufar las implementaciones reales sin tocar
 * las vistas ni este selector (R17.1, R17.5, R17.2).
 *
 * No se duplican entidades de la fundación: el runtime solo orquesta las
 * interfaces de servicio declaradas en `contracts.ts` (R17).
 */
import type {
  EventStore,
  Graph,
  MotorCasos,
  MotorInsights,
  Registry,
  MotorContextoIA,
  Copilot,
  RAG,
  Memoria,
  Twin,
  FieldOps,
  Realtime,
  Obs,
  Result,
  EventoOperacional,
  Caso,
} from "./contracts";

// ---------------------------------------------------------------------------
// Modo de runtime
// ---------------------------------------------------------------------------

/** Modo de ejecución de la Capa. */
export type RuntimeMode = "demo" | "connected";

/** Base del backend conectado (rf-api). Vacío = sin backend conectado. */
function rfBase(): string {
  return (typeof import.meta !== "undefined" && import.meta.env?.VITE_ANC_RF_BASE) || "";
}

/** ¿Está la SPA en modo DEMO explícito (`VITE_ANC_DEMO=1` o `localStorage`)? */
function isDemoFlag(): boolean {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_ANC_DEMO === "1") {
    return true;
  }
  if (typeof window !== "undefined") {
    try {
      return window.localStorage.getItem("anc.demo") === "1";
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Resuelve el modo efectivo (R17.2):
 *
 * ```text
 * VITE_ANC_DEMO === "1"   → DEMO (derivadores TS client-side)
 * ∃ VITE_ANC_RF_BASE      → CONECTADO (stub/adapters → fundación)
 * fallback                → DEMO (degradación elegante)
 * ```
 */
export function resolveMode(): RuntimeMode {
  if (isDemoFlag()) return "demo";
  if (rfBase().length > 0) return "connected";
  return "demo";
}

// ---------------------------------------------------------------------------
// Mapa de servicios y registro perezoso de proveedores
// ---------------------------------------------------------------------------

/** Asociación clave → interfaz de servicio de la Capa. */
export interface ServiceMap {
  eventStore: EventStore;
  graph: Graph;
  motorCasos: MotorCasos;
  motorInsights: MotorInsights;
  registry: Registry;
  contextoIA: MotorContextoIA;
  copilot: Copilot;
  rag: RAG;
  memoria: Memoria;
  twin: Twin;
  fieldOps: FieldOps;
  realtime: Realtime;
  obs: Obs;
}

/** Clave de un servicio de la Capa. */
export type ServiceKey = keyof ServiceMap;

/** Factoría perezosa de una implementación de servicio. */
export type ServiceProvider<K extends ServiceKey> = () => ServiceMap[K];

interface ProviderSlot<K extends ServiceKey> {
  demo?: ServiceProvider<K>;
  connected?: ServiceProvider<K>;
}

type ProviderRegistry = { [K in ServiceKey]?: ProviderSlot<K> };
type InstanceCache = { [K in ServiceKey]?: Partial<Record<RuntimeMode, ServiceMap[K]>> };

const providers: ProviderRegistry = {};
const instances: InstanceCache = {};

function slot<K extends ServiceKey>(key: K): ProviderSlot<K> {
  let s = providers[key] as ProviderSlot<K> | undefined;
  if (!s) {
    s = {};
    (providers as Record<ServiceKey, ProviderSlot<K>>)[key] = s;
  }
  return s;
}

/** Registra (perezosamente) la implementación DEMO de un servicio. */
export function provideDemo<K extends ServiceKey>(key: K, factory: ServiceProvider<K>): void {
  slot(key).demo = factory;
  // Invalida cualquier instancia DEMO cacheada para reflejar el nuevo proveedor.
  if (instances[key]) delete instances[key]!.demo;
}

/** Registra (perezosamente) la implementación CONECTADA (stub) de un servicio. */
export function provideConnected<K extends ServiceKey>(key: K, factory: ServiceProvider<K>): void {
  slot(key).connected = factory;
  if (instances[key]) delete instances[key]!.connected;
}

/**
 * Reinicia el registro y la caché de instancias.
 * Pensado para pruebas: permite registrar proveedores en cada caso.
 */
export function resetRuntime(): void {
  for (const k of Object.keys(providers) as ServiceKey[]) delete providers[k];
  for (const k of Object.keys(instances) as ServiceKey[]) delete instances[k];
}

// ---------------------------------------------------------------------------
// Resolución de servicios
// ---------------------------------------------------------------------------

/**
 * Resuelve la implementación de un servicio para el modo activo, con
 * memoización por modo y degradación elegante (R17.2, R17.6):
 *
 *  1. Modo CONECTADO → usa el proveedor conectado si existe; si no, cae
 *     al proveedor DEMO (no se crea copia divergente, R17.6).
 *  2. Modo DEMO → usa el proveedor DEMO.
 *  3. Sin proveedor aplicable → stub "no implementado aún" que devuelve
 *     `Result` de error degradado controlado (nunca rompe el build).
 */
export function getService<K extends ServiceKey>(key: K): ServiceMap[K] {
  const mode = resolveMode();
  const cache = (instances[key] ??= {}) as Partial<Record<RuntimeMode, ServiceMap[K]>>;
  const cached = cache[mode];
  if (cached) return cached;

  const s = providers[key] as ProviderSlot<K> | undefined;
  let factory: ServiceProvider<K> | undefined;
  if (mode === "connected") {
    factory = s?.connected ?? s?.demo;
  } else {
    factory = s?.demo;
  }

  const instance = factory ? factory() : (notImplemented(key) as ServiceMap[K]);
  cache[mode] = instance;
  return instance;
}

/** Accesores con nombre para cada servicio de la Capa. */
export const runtime = {
  mode: resolveMode,
  eventStore: () => getService("eventStore"),
  graph: () => getService("graph"),
  motorCasos: () => getService("motorCasos"),
  motorInsights: () => getService("motorInsights"),
  registry: () => getService("registry"),
  contextoIA: () => getService("contextoIA"),
  copilot: () => getService("copilot"),
  rag: () => getService("rag"),
  memoria: () => getService("memoria"),
  twin: () => getService("twin"),
  fieldOps: () => getService("fieldOps"),
  realtime: () => getService("realtime"),
  obs: () => getService("obs"),
} as const;

// ---------------------------------------------------------------------------
// Stubs "no implementado aún" — degradación controlada (R17.6)
// ---------------------------------------------------------------------------

/** Código de error degradado uniforme para servicios sin implementación. */
export const NOT_IMPLEMENTED = "no_implementado_aun";

function detail(key: ServiceKey): string {
  return `${NOT_IMPLEMENTED}: servicio '${key}' no registrado en el runtime`;
}

/** Result de error degradado (no lanza a través de la frontera de servicio). */
function fail<T>(key: ServiceKey): Result<T> {
  return { ok: false, error: detail(key), degraded: true };
}

/** Versión asíncrona del Result de error degradado. */
function failAsync<T>(key: ServiceKey): Promise<Result<T>> {
  return Promise.resolve(fail<T>(key));
}

/** Iterable asíncrono vacío para firmas de streaming. */
async function* emptyStream(): AsyncIterable<EventoOperacional> {
  // Sin eventos: el stub no produce datos.
}

/**
 * Construye un stub que conforma exactamente la interfaz `key`. Cada
 * método devuelve un error degradado; los métodos no basados en Result
 * adoptan el comportamiento no-destructivo más seguro.
 */
function notImplemented(key: ServiceKey): ServiceMap[ServiceKey] {
  switch (key) {
    case "eventStore": {
      const stub: EventStore = {
        append: () => failAsync(key),
        replay: () => failAsync(key),
        streamFrom: () => emptyStream(),
        verifyChain: () => failAsync(key),
      };
      return stub;
    }
    case "graph": {
      const stub: Graph = {
        upsertNode: () => failAsync(key),
        upsertEdge: () => failAsync(key),
        neighbors: () => failAsync(key),
        rootOf: () => failAsync(key),
      };
      return stub;
    }
    case "motorCasos": {
      const stub: MotorCasos = {
        ingest: () => failAsync(key),
        score: () => fail(key),
        explain: () => fail(key),
        // Orden total: sin implementación, preserva el orden de entrada (no destructivo).
        order: (casos: Caso[]) => [...casos],
        suggestActions: () => fail(key),
        timeline: () => failAsync(key),
      };
      return stub;
    }
    case "motorInsights": {
      const stub: MotorInsights = {
        correlate: () => failAsync(key),
        buildInsight: () => fail(key),
        narrative: () => fail(key),
        anomalyEvolution: () => failAsync(key),
        kpiIntelligence: () => fail(key),
        link: () => fail(key),
      };
      return stub;
    }
    case "registry": {
      const stub: Registry = {
        declare: () => fail(key),
        get: () => fail(key),
        invoke: () => failAsync(key),
        approve: () => failAsync(key),
        revert: () => failAsync(key),
      };
      return stub;
    }
    case "contextoIA": {
      const stub: MotorContextoIA = {
        assemble: () => failAsync(key),
        reconstruct: () => failAsync(key),
      };
      return stub;
    }
    case "copilot": {
      const stub: Copilot = {
        interpret: () => failAsync(key),
        proposeActions: () => failAsync(key),
      };
      return stub;
    }
    case "rag": {
      const stub: RAG = {
        index: () => failAsync(key),
        retrieve: () => failAsync(key),
        getChunk: () => failAsync(key),
      };
      return stub;
    }
    case "memoria": {
      const stub: Memoria = {
        recordDecision: () => failAsync(key),
        recordRecommendation: () => failAsync(key),
        recordOutcome: () => failAsync(key),
        rates: () => failAsync(key),
      };
      return stub;
    }
    case "twin": {
      const stub: Twin = {
        simulate: () => failAsync(key),
        replay: () => failAsync(key),
        forecast: () => failAsync(key),
      };
      return stub;
    }
    case "fieldOps": {
      const stub: FieldOps = {
        track: () => failAsync(key),
        route: () => failAsync(key),
        associateEvidence: () => failAsync(key),
        reconcile: () => failAsync(key),
      };
      return stub;
    }
    case "realtime": {
      const stub: Realtime = {
        subscribe: () => failAsync(key),
        resume: () => failAsync(key),
      };
      return stub;
    }
    case "obs": {
      const stub: Obs = {
        trace: () => failAsync(key),
        metric: () => failAsync(key),
        aiMetrics: () => failAsync(key),
        raiseIncident: () => failAsync(key),
      };
      return stub;
    }
    default: {
      // Exhaustividad: si se añade un servicio nuevo, el compilador lo exige aquí.
      const _exhaustive: never = key;
      throw new Error(`servicio desconocido: ${String(_exhaustive)}`);
    }
  }
}
